import React, { useContext, useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Grid, useMediaQuery } from '@mui/material';
import { useTranslation } from '../../shared/hooks';
import * as translations from './intl';
import { GeneralContext } from '../../shared/contexts/GeneralContext.context';
import {
  Button,
  ConfirmationModal,
  PrivacyPolicy,
  TermsAndConditions,
} from '..';
import './style.css';

const PWA_URLS = [
  'https://axapartners-eu-teleconsultation.axa.travel',
  'https://axapartners-eu-test-teleconsultation.axa.travel',
];

const FooterContainer = styled(Grid)(({ theme }) => ({
  flexShrink: 0,
  width: '100%',
  alignItems: 'center',
  display: 'flex',
  background: theme.palette.primary.footerbackgroundcolor,
  color: theme.palette.text.main,
}));

const FooterContainerConfirm = styled(Grid)(({ theme }) => ({
  width: '100%',
  alignItems: 'center',
  display: 'flex',
  background: theme.palette.primary.footerbackgroundcolor,
  color: theme.palette.text.main,
  bottom: '0',
}));

const FooterLink = styled('a')({
  textDecoration: 'none',
  color: 'white',
  padding: '0.5em',
  fontFamily: 'Source Sans Pro',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'none',
    color: 'white',
  },
});

const FooterCopyright = styled('p')({
  textDecoration: 'none',
  color: 'white',
  padding: '10px 6em 0 0',
  fontFamily: 'Source Sans Pro',
});

const FooterCopyrightMobile = styled('p')({
  textDecoration: 'none',
  color: 'white',
  padding: '0.5em',
  fontFamily: 'Source Sans Pro',
});

const Pipe = styled('span')({
  textDecoration: 'none',
  color: 'white',
  padding: '0em',
  margin: '0',
});

const SplitRow = styled(Grid)({
  borderBottom: '1px solid white',
  padding: '15px 0 15px 6em',
  width: '100%',
});

const SplitMobileRow = styled(Grid)({
  borderBottom: '1px solid white',
  width: '100%',
});

const MobileItem = styled(Grid)(({ theme }) => ({
  padding: 10,
  [theme.breakpoints.down('md')]: {
    padding: '10px 0px',
    margin: '0px 10px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
  },
}));

function TermsConditions({ label, onClick }) {
  return <FooterLink onClick={onClick}>{label}</FooterLink>;
}

function Privacy({ label, onClick }) {
  return <FooterLink onClick={onClick}>{label}</FooterLink>;
}

function Cookies({ label, onClick }) {
  return (
    <FooterLink
      onClick={() => {
        window.location.href = '/cookie-policy';
      }}
    >
      {label}
    </FooterLink>
  );
}

export default function Footer() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const { locale: intl } = useTranslation({
    translationsFile: translations,
  });

  const {
    TERMS_AND_CONDITIONS,
    PRIVACY,
    COOKIES: COOKIES_LABEL,
    COOKIE_SETTINGS,
    PRIVACY_POLICY,
  } = intl;

  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showCookiesPolicy, setShowCookiesPolicy] = useState(false);
  const [showCookiesPolicyGlobalUS, setShowCookiesPolicyGlobalUS] =
    useState(false);
  const [showCookiesPolicyTripUK, setShowCookiesPolicyTripUK] = useState(false);
  const [showCookiesPolicyGeneric, setShowCookiesPolicyGeneric] =
    useState(false);
  const [showCookiesPolicyUSChubb, setShowCookiesPolicyUSChubb] =
    useState(false);
  const [showCookiesPolicyUSMetlife, setShowCookiesPolicyUSMetlife] =
    useState(false);
  const [showCookiesPolicyUSTI, setShowCookiesPolicyUSTI] = useState(false);
  const [showCookiesPolicyWwiis, setShowCookiesPolicyWwiis] = useState(false);
  const [canInstall, setCanInstall] = useState(!!window.deferredInstallPrompt);
  const [isInstalled, setIsInstalled] = useState(
    localStorage.getItem('pwaInstalled') === 'true'
  );
  const [showIosInstallHelp, setShowIosInstallHelp] = useState(false);

  const getIsIos = () => {
    const ua = window.navigator.userAgent || '';
    const iOSByUA = /iPad|iPhone|iPod/i.test(ua);
    const iPadOS13Plus =
      window.navigator.platform === 'MacIntel' &&
      window.navigator.maxTouchPoints > 1;
    return iOSByUA || iPadOS13Plus;
  };

  const isStandaloneMode = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

  const [isIosDevice] = useState(getIsIos());

  useEffect(() => {
    if (!PWA_URLS.includes(window.location.origin)) return;

    const update = () => {
      setCanInstall(!!window.deferredInstallPrompt);
      setIsInstalled(
        isStandaloneMode() || localStorage.getItem('pwaInstalled') === 'true'
      );
    };

    window.addEventListener('pwaInstallAvailable', update);
    update();

    return () => window.removeEventListener('pwaInstallAvailable', update);
  }, []);

  const handleToggleTerms = () => {
    setShowTerms((prev) => !prev);
    if (!showTerms) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        zone: 'footer',
        element_clicked: 'faq',
        event: 'navigation',
      });
    }
  };

  const handleTogglePrivacy = () => {
    setShowPrivacy((prev) => !prev);
    if (!showPrivacy) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        zone: 'footer',
        element_clicked: 'data privacy policy',
        event: 'navigation',
      });
    }
  };

  const handleToggleCookiesPolicy = () => {
    const genericDomain = import.meta.env.VITE_DOMAIN_GENERIC;
    const tripUKDomain = import.meta.env.VITE_DOMAIN_TRIPUK;
    const globalUSDomain = import.meta.env.VITE_DOMAIN_US;
    const chubbDomain = import.meta.env.VITE_DOMAIN_US_CHUBB;
    const metlifeDomain = import.meta.env.VITE_DOMAIN_US_METLIFE;
    const ustiDomain = import.meta.env.VITE_DOMAIN_USTI;
    const wwiisDomain = import.meta.env.VITE_DOMAIN_WWIIS;
    const currentDomain = window.location.hostname;

    let showDialog = false;

    if (currentDomain === genericDomain) {
      showDialog = !showCookiesPolicyGeneric;
    } else if (currentDomain === tripUKDomain) {
      showDialog = !showCookiesPolicyTripUK;
    } else if (currentDomain === globalUSDomain) {
      showDialog = !showCookiesPolicyGlobalUS;
    } else if (currentDomain === chubbDomain) {
      showDialog = !showCookiesPolicyUSChubb;
    } else if (currentDomain === metlifeDomain) {
      showDialog = !showCookiesPolicyUSMetlife;
    } else if (currentDomain === ustiDomain) {
      showDialog = !showCookiesPolicyUSTI;
    } else if (currentDomain === wwiisDomain) {
      showDialog = !showCookiesPolicyWwiis;
    } else {
      showDialog = !showCookiesPolicy;
    }

    if (showDialog) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        zone: 'footer',
        element_clicked: 'cookies policy',
        event: 'navigation',
      });
    }

    if (currentDomain === genericDomain) {
      setShowCookiesPolicyGeneric((prev) => !prev);
    } else if (currentDomain === tripUKDomain) {
      setShowCookiesPolicyTripUK((prev) => !prev);
    } else if (currentDomain === globalUSDomain) {
      setShowCookiesPolicyGlobalUS((prev) => !prev);
    } else if (currentDomain === chubbDomain) {
      setShowCookiesPolicyUSChubb((prev) => !prev);
    } else if (currentDomain === metlifeDomain) {
      setShowCookiesPolicyUSMetlife((prev) => !prev);
    } else if (currentDomain === ustiDomain) {
      setShowCookiesPolicyUSTI((prev) => !prev);
    } else if (currentDomain === wwiisDomain) {
      setShowCookiesPolicyWwiis((prev) => !prev);
    } else {
      setShowCookiesPolicy((prev) => !prev);
    }
  };

  const handleAddToHomeScreen = async () => {
    const promptEvent = window.deferredInstallPrompt;
    if (!promptEvent) return;

    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem('pwaInstalled', 'true');
      setIsInstalled(true);
    }

    window.deferredInstallPrompt = null;
    setCanInstall(false);
  };

  const handleOpenApp = () => {
    const anchor = document.createElement('a');
    anchor.href = window.location.origin;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleAddToHomeClick = async () => {
    if (isIosDevice) {
      setShowIosInstallHelp(true);
      return;
    }

    await handleAddToHomeScreen();
  };

  const showAddToHome = canInstall || (isIosDevice && !isStandaloneMode());
  const showOpenApp =
    !canInstall && !isIosDevice && isInstalled && !isStandaloneMode();

  const { userData } = useContext(GeneralContext);
  const ContainerComponent = userData?.email_verified
    ? FooterContainer
    : FooterContainerConfirm;

  return (
    <>
      <TermsAndConditions
        showTerms={showTerms}
        handleShowTerms={handleToggleTerms}
      />
      <PrivacyPolicy
        showPrivacy={showPrivacy}
        handleShowPrivacy={handleTogglePrivacy}
      />
      <ContainerComponent data-testid="footer">
        <Grid container sx={{ width: '100%' }}>
          {isDesktop ? (
            <>
              <SplitRow
                container
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid>
                  <TermsConditions
                    label={TERMS_AND_CONDITIONS}
                    onClick={handleToggleTerms}
                  />
                </Grid>
                <Grid>
                  <Pipe>|</Pipe>
                </Grid>
                <Grid>
                  <Privacy label={PRIVACY} onClick={handleTogglePrivacy} />
                </Grid>
                <Grid>
                  <Pipe>|</Pipe>
                </Grid>
                <Grid>
                  <Cookies
                    label={COOKIES_LABEL}
                    onClick={handleToggleCookiesPolicy}
                  />
                </Grid>
                <Grid>
                  <Pipe>|</Pipe>
                </Grid>
                <Grid>
                  <Button
                    id="ot-sdk-btn"
                    className="ot-sdk-show-settings"
                    name="cookie"
                    text={COOKIE_SETTINGS}
                  />
                </Grid>
                {showAddToHome && (
                  <>
                    <Grid>
                      <Pipe>|</Pipe>
                    </Grid>
                    <Grid>
                      <FooterLink onClick={handleAddToHomeClick}>
                        Add to Home Screen
                      </FooterLink>
                    </Grid>
                  </>
                )}
                {showOpenApp && (
                  <>
                    <Grid>
                      <Pipe>|</Pipe>
                    </Grid>
                    <Grid>
                      <FooterLink onClick={handleOpenApp}>Open App</FooterLink>
                    </Grid>
                  </>
                )}
              </SplitRow>
              <Grid
                container
                justifyContent="flex-end"
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <FooterCopyright>{PRIVACY_POLICY}</FooterCopyright>
              </Grid>
            </>
          ) : (
            <>
              <SplitMobileRow container direction="column">
                <MobileItem>
                  <TermsConditions
                    label={TERMS_AND_CONDITIONS}
                    onClick={handleToggleTerms}
                  />
                </MobileItem>
                <MobileItem>
                  <Privacy label={PRIVACY} onClick={handleTogglePrivacy} />
                </MobileItem>
                <MobileItem>
                  <Cookies
                    label={COOKIES_LABEL}
                    onClick={handleToggleCookiesPolicy}
                  />
                </MobileItem>
                <Grid>
                  <Button
                    id="ot-sdk-btn"
                    className="ot-sdk-show-settings"
                    name="cookie"
                    text={COOKIE_SETTINGS}
                  />
                </Grid>
                {showAddToHome && (
                  <MobileItem>
                    <FooterLink onClick={handleAddToHomeClick}>
                      Add to Home Screen
                    </FooterLink>
                  </MobileItem>
                )}
                {showOpenApp && (
                  <MobileItem>
                    <FooterLink onClick={handleOpenApp}>Open App</FooterLink>
                  </MobileItem>
                )}
              </SplitMobileRow>
              <Grid container justifyContent="center" alignItems="center">
                <FooterCopyrightMobile>{PRIVACY_POLICY}</FooterCopyrightMobile>
              </Grid>
            </>
          )}
        </Grid>
      </ContainerComponent>
      <ConfirmationModal
        open={showIosInstallHelp}
        handleClose={() => setShowIosInstallHelp(false)}
        handleAction={() => setShowIosInstallHelp(false)}
        okButton
        title="Add to Home Screen"
        text={
          'To install on iPhone/iPad:<br/><br/>' +
          '1. Tap the Share button in Safari.<br/>' +
          '2. Scroll and tap Add to Home Screen.<br/>' +
          '3. Tap Add.'
        }
      />
    </>
  );
}
